import type { DatasetColumn } from './reportDatasets'
import type { ReportDatasetKey } from './reportStorage'

// PR 3 (findings #220, #221): what the report and widget builders offer.
//
// - Internal columns (ids, codes) are never shown or exported. A saved report
//   or widget that names one keeps working, it just no longer shows it.
// - Retired columns are no longer offered, but a report that already has one
//   keeps it until the office removes it (`fishStock`: "Stock" now holds the
//   exact count on its day and the calculated stock on the others).

const INTERNAL_COLUMN_KEYS = new Set([
  'id',
  'sourceKey',
  'farmId',
  'productionUnitId',
  'lotId',
  'causeCode',
  'convertedTotalCostsCents',
  'fxRateSnapshotId',
  'fxRateSourceId',
  'fxRateSourceVersion',
  'fxRoundingPolicyVersion'
])

const RETIRED_COLUMN_KEYS = new Set(['fishStock'])

export const isInternalColumn = (key: string): boolean => INTERNAL_COLUMN_KEYS.has(key)

/** The columns a builder offers; anything in `keep` stays so a saved choice is not lost. */
export const offeredColumns = <T extends { key: string }>(columns: readonly T[], keep: readonly string[] = []): T[] => {
  const kept = new Set(keep)
  return columns.filter((column) => (
    kept.has(column.key) || (!isInternalColumn(column.key) && !RETIRED_COLUMN_KEYS.has(column.key))
  ))
}

/** What a new report starts with; the two lot labels only repeat Lote and Unidad. */
export const defaultReportColumns = (columns: readonly { key: string }[]): string[] => (
  offeredColumns(columns).map((column) => column.key).filter((key) => key !== 'lotUnit' && key !== 'lotOrigin').slice(0, 5)
)

/** A report's columns in the order the office chose, without internal ones. */
export const orderedReportColumns = <T extends { key: string }>(columns: readonly T[], keys: readonly string[]): T[] => {
  const byKey = new Map(columns.map((column) => [column.key, column] as const))
  const ordered: T[] = []
  for (const key of new Set(keys)) {
    const column = byKey.get(key)
    if (column && !isInternalColumn(key)) ordered.push(column)
  }
  return ordered
}

export const moveColumn = (keys: readonly string[], key: string, offset: number): string[] => {
  const from = keys.indexOf(key)
  const to = from + offset
  if (from < 0 || to < 0 || to >= keys.length) return keys.slice()
  const next = keys.slice()
  next.splice(from, 1)
  next.splice(to, 0, key)
  return next
}

/** Drops `key` where `target` is, for drag and drop. */
export const moveColumnTo = (keys: readonly string[], key: string, target: string): string[] => {
  const to = keys.indexOf(target)
  return to < 0 ? keys.slice() : moveColumn(keys, key, to - keys.indexOf(key))
}

export type ReportColumnKind = 'number' | 'text' | 'date' | 'datetime' | 'calculated'

export const COLUMN_KIND_ICON: Record<ReportColumnKind, string> = {
  number: '#',
  text: 'abc',
  date: '📅',
  datetime: '🕐',
  calculated: '🧮'
}

const DATETIME_KEY = /(At|AtLocal|RecordLocal)$/
const DATE_KEY = /(^date$|Date$|^dueAt$|On$|AsOf$|From$)/
const TEXT_KEY = /(^lot|^stage$|^lifeStage$|Name$|Label$|Key$|Band$|Status$|Source$|Reason$|^week|^cause$|^notes$|^upVol$|^mort$|^health|Type|Types$|^title$|^tags$|^description$|^category$|^unit$|^name$|^species|Supplier|supplier|^strain$|^broodstockGroup$|^stockTraits$|^quarantine$|^recordKind$|By$|^restored$|^lateEntry$|^completed$|^lowConfidence$|^currency|Currency$|Breakdown$|^mixName$|^feedingResponse$|^feedLotNumber$|^healthCertificateNo$|^disinfectionProduct$|^captureSourceLabel$|^expectedFcrRangeLabel$)/

// `dueAt` is a task's day, and the ordering above matters: "Date" columns win
// over the generic text pattern, and a calculated value says so whatever its shape.
export const columnKind = (column: Pick<DatasetColumn, 'key' | 'derived'>): ReportColumnKind => {
  const { key, derived } = column
  if (DATE_KEY.test(key)) return 'date'
  if (DATETIME_KEY.test(key)) return 'datetime'
  if (derived) return 'calculated'
  return TEXT_KEY.test(key) ? 'text' : 'number'
}

export type ReportDatasetGroupKey = 'produccion' | 'alimento' | 'agua' | 'costos' | 'registros'

export const REPORT_DATASET_GROUPS: Array<{ key: ReportDatasetGroupKey; datasets: ReportDatasetKey[] }> = [
  { key: 'produccion', datasets: ['productionDatatable', 'lots', 'lotGrowthPerformance', 'lotProjection', 'lotHatchery', 'weightSampleIndividual'] },
  { key: 'alimento', datasets: ['feed', 'feedEvents', 'feedDailyTotals'] },
  { key: 'agua', datasets: ['water'] },
  { key: 'costos', datasets: ['lotCostPerKg'] },
  { key: 'registros', datasets: ['mortalityByCause', 'mortalityEvents', 'mortalityRecording', 'deletedLots', 'inventory', 'tasks'] }
]
